import { filter, map, reduce, size } from 'lodash/fp';
import * as urlBuilder from '../../../../../common/services/url-builder';
import * as genderService from '../../../../../react/services/Gender';
import r from '../../../../../react/services/Replacement';
import UserLink from '../../../../components/UserLink';
import { getCurrentUser } from '../../../../services/CurrentUser';
import { isFeatureEnabled } from '../../../../services/Features';

export function transformMessageTargets({ targets }) {
    if (size(targets) > 0) {
        const transformedTargets = transformTargets({ targets });
        return <span>about {transformedTargets}</span>;
    }
    return <noscript />;
}

export function transformPraiseTargets({ targets }) {
    if (size(targets) > 0) {
        const transformedTargets = transformTargets({ targets });
        return <span>to {transformedTargets}</span>;
    }
    return <noscript />;
}

export function transformTargets({ targets }) {
    const sizeTargets = size(targets);
    if (sizeTargets === 1) {
        const user = targets[0];
        return <UserLink user={user} />;
    }
    if (sizeTargets === 2) {
        const user1 = targets[0];
        const user2 = targets[1];
        return (
            <span>
                <UserLink user={user1} /> and <UserLink user={user2} />
            </span>
        );
    }
    if (sizeTargets > 2) {
        let user1 = targets[0];
        const count = sizeTargets - 1;
        const currentUser = getCurrentUser() || {};
        const res = findUser(targets, currentUser);
        if (size(res) === 1) {
            user1 = res[0];
        }
        return (
            <span>
                <UserLink user={user1} /> and {count} other people
            </span>
        );
    }
    return <noscript />;
}

export function transformTarget(activityItem) {
    const { target } = activityItem;
    return <UserLink user={target} />;
}

export function transformActorName(actor) {
    if (!actor) {
        return <span>An anonymous user</span>;
    }
    const { name } = actor;
    if (isFeatureEnabled('FEATURE_inboxPage')) {
        return <span>{name}</span>;
    }
    // @todo don't use strong here!
    return <strong>{name}</strong>;
}

export function countActivities({ items }) {
    return reduce(
        (accumulator, value) => accumulator + value,
        0,
        map(({ activities }) => activities.length, items)
    );
}

function findUser(targets, user) {
    return filter(target => target.id === user.id, targets);
}

function isPossessivePronoun(value) {
    return value === 'his' || value === 'her';
}

export function transformObjectiveOwner({ actor, content }) {
    const { objective } = content;
    let user = {
        displayedName: transformOwner(objective, actor),
        ...objective.owner,
    };
    if (isPossessivePronoun(user.displayedName)) {
        return <span>{user.displayedName}</span>;
    }
    if (!isCurrentUser(objective.owner)) {
        user.link = urlBuilder.linkToNewUserProfile(objective.owner.id);
    }
    return <UserLink user={user} />;
}

export function transformOwnerLink(owner) {
    const link = !isCurrentUser(owner) ? urlBuilder.linkToNewUserProfile(owner.id) : null;
    const user = {
        displayedName: transformOwner({ owner }),
        link,
    };
    return <UserLink user={user} />;
}

export function transformOwner({ owner }, actor) {
    if (isCurrentUser(owner)) {
        return 'your';
    }
    if (actor && owner && actor.id === owner.id && owner.gender) {
        return genderService.asPossessivePronoun(owner.gender.toLowerCase());
    }
    return genderService.asGenitive(owner.name || owner.firstName);
}

export function transformChecked({ change }) {
    return change.newValue === 'true' ? 'checked' : 'unchecked';
}

export function transformObjectiveContentTitle({ content }) {
    const kind = content.content.kind;
    if (kind === 'KEY_RESULTS') {
        return `${r('objective.article.key-result')} ${r('objective.singular.key-result')}`;
    }
    if (kind === 'ACTIONS') {
        return 'an Action Item';
    }
    if (kind === 'NEEDS') {
        return 'a Need';
    }
    return `${r('objective.article.key-result')} ${r('objective.singular.key-result')}`;
}

export function findChecklistChange({ type, change }) {
    if (type === 'OBJECTIVE_CHECKLIST_TOGGLED') {
        return change.newValue === 'true' ? 'checked' : 'unchecked';
    }
    if (type === 'OBJECTIVE_LIST_ITEM_ADDED') {
        return 'added';
    }
    if (type === 'OBJECTIVE_LIST_ITEM_REMOVED') {
        return 'removed';
    }
}

export function isCurrentUser(user) {
    const currentUser = getCurrentUser();
    return currentUser && currentUser.id === user.id;
}
